/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { KTSVG } from '../../../../../_metronic/helpers';
import { api } from '../../../../modules/api/api';
import { useAuth } from '../../../../modules/auth';
import { useToast } from '../../../../modules/components/toast/ToastContext';
import InfoTooltip from '../../../../modules/components/tooltip/InfoTooltip';
import { SummaryRow } from '../detail/table/SummaryTable';

type Props = {
  data: any;
  selectedResult: SummaryRow;
  show: boolean;
  handleClose: () => void;
  handleRefetch: () => void;
};

const AddResultRemarkModal: React.FC<Props> = ({
  data,
  selectedResult,
  show,
  handleClose,
  handleRefetch,
}) => {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const schema = z.object({
    remark: z.string().min(1),
    url: z.string().min(1),
    user: z.string().min(1),
  });
  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      remark: '',
      url: '',
      user: '', // TODO: add current user's name by default and disabled field
    },
  });

  const onClose = (result) => {
    reset();
    setErrorMessage('');
    handleClose();
  };

  const onSubmit: SubmitHandler<FormData> = async (formData) => {
    try {
      setIsLoading(true);
      const { result, status } = await api(
        `admin/v1/add_site_intelligence_result_remark`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            request_id: data?.request_id,
            queue_id: data?.queue_id,
            result_key: selectedResult?.remarkKey,
            ...formData,
          }),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence',
      ).then((resp) => {
        return { result: resp?.json(), status: resp?.status };
      });
      const resolvedResult = await result;
      if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
        setToast({
          show: true,
          type: 'Success',
          header: intl.formatMessage({ id: 'MESSAGE.SUCCESSFUL' }),
          message: intl.formatMessage({ id: 'MESSAGE.UPDATE_SUCCESS' }),
        });
        onClose(true);
        handleRefetch();
      } else {
        setErrorMessage(resolvedResult.message);
      }
    } catch (err) {
      setErrorMessage(intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      id='add_result_remark_modal'
      tabIndex={-1}
      dialogClassName='modal-dialog-centered mw-1000px h-auto'
      show={show}
    >
      <div className='container-xxl px-10 py-10'>
        <div className='modal-header py-2 d-flex border-0'>
          {/* begin::Close */}
          <div>
            <h1 className='d-flex align-items-center text-dark fw-bolder my-1 fs-3'>Add Remark</h1>
          </div>
          <div className=' mr-auto btn btn-icon btn-sm btn-light-primary' onClick={onClose}>
            <KTSVG className='svg-icon-2' path='/media/icons/duotune/arrows/arr061.svg' />
          </div>
          {/* end::Close */}
        </div>
        <div className='modal-body'>
          <div className='d-flex flex-column mb-9'>
            <label className='form-label'>Add Remark For:</label>
            <div className='card card-custom card-flush border border-gray-400 overlay overlay-block'>
              <div className='card-body py-4 px-5 overlay-wrapper cursor-default d-flex gap-9'>
                <div className='fw-bold'>{selectedResult?.field}</div>
                <div>{selectedResult?.render()}</div>
              </div>
            </div>
          </div>

          <div className='form'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div className='d-flex mb-4'>
                  <label className='form-label w-50 required'>Remark</label>
                  <Controller
                    name='remark'
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <textarea
                          {...field}
                          placeholder='Enter your remark/corrections here'
                          className={
                            fieldState.invalid
                              ? 'form-control border border-danger'
                              : 'form-control'
                          }
                          disabled={isLoading}
                        />
                      );
                    }}
                  />
                </div>
              </div>

              <div>
                <div className='d-flex mb-4'>
                  <label className='form-label w-50 mt-2 required d-flex align-items-center'>
                    Source URL
                  </label>
                  <Controller
                    name='url'
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <input
                          {...field}
                          placeholder='http://buessstore.com'
                          type='text'
                          className={
                            fieldState.invalid
                              ? 'form-control border border-danger'
                              : 'form-control'
                          }
                        />
                      );
                    }}
                  />
                </div>
              </div>

              {/* TODO: disable/remove field once have auth. Will be current user's name by default */}
              <div>
                <div className='d-flex mb-4'>
                  <label className='form-label w-50 mt-2 required'>Remarked By</label>
                  <Controller
                    name='user'
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <input
                          {...field}
                          type='text'
                          placeholder='Enter your name here'
                          className={
                            fieldState.invalid
                              ? 'form-control border border-danger'
                              : 'form-control'
                          }
                        />
                      );
                    }}
                  />
                </div>
              </div>

              {errorMessage ? (
                <div className='text-danger mb-4 border border-danger rounded py-2 px-2'>
                  {errorMessage}
                </div>
              ) : (
                <></>
              )}
              <button className='btn btn-primary' type='submit' disabled={isLoading}>
                {intl.formatMessage({ id: 'COMMON.SUBMIT' })}
              </button>
              <button
                className='btn btn-secondary mx-2'
                type='button'
                onClick={onClose}
                disabled={isLoading}
              >
                {intl.formatMessage({ id: 'COMMON.CANCEL' })}
              </button>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { AddResultRemarkModal };
